import * as THREE from 'three';
import { rnd, rr, TAU } from './util.js';

/** Small helpers that paint procedural textures on a canvas. */

// Older Safari versions lack CanvasRenderingContext2D.roundRect
if (typeof CanvasRenderingContext2D !== 'undefined' && !CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r = 0) {
    const rad = Math.min(typeof r === 'number' ? r : r[0] || 0, w / 2, h / 2);
    this.moveTo(x + rad, y);
    this.arcTo(x + w, y, x + w, y + h, rad);
    this.arcTo(x + w, y + h, x, y + h, rad);
    this.arcTo(x, y + h, x, y, rad);
    this.arcTo(x, y, x + w, y, rad);
    this.closePath();
    return this;
  };
}

function make(w, h, draw, { repeat = false, srgb = true, aniso = 8, nearest = false } = {}) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  draw(g, w, h);
  const t = new THREE.CanvasTexture(c);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = aniso;
  if (nearest) t.magFilter = THREE.NearestFilter;
  t.needsUpdate = true;
  return t;
}

function speckle(g, w, h, n, colors, minS = 1, maxS = 2, alpha = 0.5) {
  for (let i = 0; i < n; i++) {
    g.globalAlpha = alpha * rnd();
    g.fillStyle = colors[Math.floor(rnd() * colors.length)];
    const s = rr(minS, maxS);
    g.fillRect(rnd() * w, rnd() * h, s, s);
  }
  g.globalAlpha = 1;
}

/** Asphalt with gravel shoulders, dashed centre line and edge lines. u across, v along (one 6 m period). */
export function asphaltTexture() {
  return make(128, 256, (g, w, h) => {
    g.fillStyle = '#cdb98c';
    g.fillRect(0, 0, w, h);
    speckle(g, w, h, 500, ['#b19a6c', '#e0d0a4', '#9d8a62'], 1, 3, 0.7);
    // asphalt
    const a0 = 6, a1 = w - 6;
    g.fillStyle = '#6e6b7a';
    g.beginPath();
    g.roundRect(a0, -4, a1 - a0, h + 8, 3);
    g.fill();
    speckle(g, w, h, 1400, ['#5f5c6c', '#7d7a8a', '#57546a', '#8a8797'], 1, 2, 0.55);
    // edge lines
    g.fillStyle = '#efe7cb';
    g.fillRect(a0 + 6, 0, 3, h);
    g.fillRect(a1 - 9, 0, 3, h);
    // dashed centre
    g.fillStyle = '#ffd95a';
    g.fillRect(w / 2 - 2, 0, 4, h * 0.5);
  }, { repeat: true });
}

/** Dirt track (village spokes). */
export function dirtTexture() {
  return make(128, 128, (g, w, h) => {
    g.fillStyle = '#c9a877';
    g.fillRect(0, 0, w, h);
    speckle(g, w, h, 700, ['#b48f5f', '#dcc08c', '#a17f52', '#e4cfa0'], 1, 3, 0.7);
    // darker wheel ruts
    g.globalAlpha = 0.22;
    g.fillStyle = '#8f6f45';
    g.fillRect(w * 0.26, 0, 9, h);
    g.fillRect(w * 0.66, 0, 9, h);
    g.globalAlpha = 1;
    // soft edges fade to grass-ish sand
    const grd = g.createLinearGradient(0, 0, w, 0);
    grd.addColorStop(0, 'rgba(150,180,90,0.55)');
    grd.addColorStop(0.16, 'rgba(150,180,90,0)');
    grd.addColorStop(0.84, 'rgba(150,180,90,0)');
    grd.addColorStop(1, 'rgba(150,180,90,0.55)');
    g.fillStyle = grd;
    g.fillRect(0, 0, w, h);
  }, { repeat: true });
}

/** Round plaza cobbles with a sunburst ring. */
export function cobbleTexture() {
  return make(1024, 1024, (g, w, h) => {
    g.fillStyle = '#7d7466';
    g.fillRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2, R = w / 2;
    const stones = ['#c9bda6', '#bcae94', '#d6cbb5', '#b3a48a', '#c4b79c', '#a99b83'];
    // concentric rings of stones
    for (let ring = 0; ring < 22; ring++) {
      const r0 = (ring / 22) * R, r1 = ((ring + 1) / 22) * R;
      const rm = (r0 + r1) / 2;
      const count = Math.max(6, Math.floor((TAU * rm) / ((r1 - r0) * 1.35)));
      const off = rnd() * TAU;
      for (let i = 0; i < count; i++) {
        const a0 = off + (i / count) * TAU, a1 = off + ((i + 1) / count) * TAU;
        const gap = 0.012 + 0.004 * rnd();
        g.fillStyle = stones[Math.floor(rnd() * stones.length)];
        g.beginPath();
        g.arc(cx, cy, r1 - 2, a0 + gap, a1 - gap);
        g.arc(cx, cy, r0 + 2, a1 - gap, a0 + gap, true);
        g.closePath();
        g.fill();
      }
    }
    // decorative ring
    g.lineWidth = 16;
    g.strokeStyle = '#e8b04a';
    g.beginPath();
    g.arc(cx, cy, R * 0.66, 0, TAU);
    g.stroke();
    g.lineWidth = 8;
    g.strokeStyle = '#d95f5f';
    g.beginPath();
    g.arc(cx, cy, R * 0.63, 0, TAU);
    g.stroke();
    // sun rays
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * TAU;
      g.strokeStyle = '#e8b04a';
      g.lineWidth = 10;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * R * 0.2, cy + Math.sin(a) * R * 0.2);
      g.lineTo(cx + Math.cos(a) * R * 0.5, cy + Math.sin(a) * R * 0.5);
      g.stroke();
    }
    speckle(g, w, h, 4000, ['#6f6656', '#e0d6c0'], 1, 3, 0.35);
    // outer dark rim (curb shadow)
    const rg = g.createRadialGradient(cx, cy, R * 0.9, cx, cy, R);
    rg.addColorStop(0, 'rgba(60,50,40,0)');
    rg.addColorStop(1, 'rgba(60,50,40,0.5)');
    g.fillStyle = rg;
    g.fillRect(0, 0, w, h);
  });
}

/** Greyscale row stripes for crop fields (tinted by material colour). */
export function rowsTexture() {
  return make(64, 64, (g, w, h) => {
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, w, h);
    g.fillStyle = '#cfcfcf';
    g.fillRect(0, 0, w * 0.5, h);
    g.fillStyle = '#e6e6e6';
    g.fillRect(w * 0.5 - 2, 0, 3, h);
    speckle(g, w, h, 120, ['#bdbdbd', '#ffffff'], 1, 2, 0.6);
  }, { repeat: true });
}

/** Flat soft blob used for shadows / glows (white with radial alpha). */
export function blobTexture(size = 128, inner = 0.0) {
  return make(size, size, (g, w, h) => {
    const grd = g.createRadialGradient(w / 2, h / 2, w * inner * 0.5, w / 2, h / 2, w / 2);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, w, h);
  }, { srgb: false });
}

/** Glyph (emoji / text) on a transparent canvas, for sprites. */
export function glyphTexture(text, { size = 128, color = '#fff', font = '700 96px Fredoka, Arial Rounded MT Bold, sans-serif', stroke = '#20233f', strokeW = 10 } = {}) {
  return make(size, size, (g, w, h) => {
    g.font = font;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    if (stroke) {
      g.lineWidth = strokeW;
      g.strokeStyle = stroke;
      g.strokeText(text, w / 2, h / 2 + 4);
    }
    g.fillStyle = color;
    g.fillText(text, w / 2, h / 2 + 4);
  });
}

export function makeCanvasTexture(w, h, draw, opts) {
  return make(w, h, draw, opts);
}
